import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import { Button } from '@repo/studio-design-system-ui-button'
import { useIsLoggedInQuery } from 'auth/hooks'
import { UserAuthModalContext } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useCoinSubscriptionQuery } from '@repo/cf-user/hooks/use-user-data'
import { LFS_URL } from '@repo/studio-utils/config/env'
import { CurrencyCode } from '@repo/graphql-types'
import type { CoinPack } from '@repo/graphql-types'
import { useCoinTransferAnimation } from '@repo/studio-hooks-coins/hooks/use-coin-transfer-animation'
import { usePurchaseCoins } from '@repo/studio-hooks-coins/hooks/use-purchase-coins'
import { formatCurrency, formatUnitPrice } from '@repo/studio-utils/utils/currency'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import type { DefaultPlan } from 'payments/payments-dialog/lib/plans'
import * as Dialog from 'primitives/dialog'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { useSwapCoinPlan } from '../hooks/use-swap-coin-plan'
import { useAutostartCoinPlan } from '../hooks/use-autostart-coin-plan'
import { useCoinPlanMutationPending } from '../hooks/use-coin-plan-mutation-pending'
import {
  SUBSCRIPTION_TIERS,
  ONE_TIME_PACKS,
  PLAN_TO_PRICE,
  isCoinPlan,
} from '@repo/studio-hooks-coins/constants/coin-top-up'
import { CoinPackageCard } from './coin-package-card'
import { SubscriptionCard } from './subscription-card'

const COIN_GRAPHIC = `${LFS_URL}/studio/coins/coins-dialog-bg.webp`

export function CoinTopUpDialogContent({
  onFallbackToCheckout,
  onClose,
  onAutoStartSuccess,
}: {
  onFallbackToCheckout: (plan: DefaultPlan) => void
  onClose: () => void
  onAutoStartSuccess?: () => void
}) {
  const t = useStudioTranslate()
  const { currency } = usePricesForUser()
  const displayCurrencyCode = currency.code ?? CurrencyCode.Usd
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const loginModal = React.useContext(UserAuthModalContext)
  const { data: coinSub } = useCoinSubscriptionQuery()
  const swap = useSwapCoinPlan()
  const autostart = useAutostartCoinPlan(onFallbackToCheckout, onAutoStartSuccess)
  const isPending = useCoinPlanMutationPending()

  const isSubscribed = coinSub?.isSubscribed ?? false
  const successPlan = swap.successPlan ?? autostart.successPlan

  function selectPlan(plan: DefaultPlan) {
    if (!isLoggedIn) {
      loginModal?.openModal()
      return
    }
    if (!isCoinPlan(plan)) {
      onFallbackToCheckout(plan)
      return
    }
    if (isSubscribed) {
      swap.swapToPlan(plan)
    } else {
      autostart.startPlan(plan)
    }
  }

  const resetSwap = swap.reset
  const resetAutostart = autostart.reset

  React.useEffect(() => {
    if (!successPlan) {
      return
    }
    onClose()
    resetSwap()
    resetAutostart()
  }, [successPlan, resetSwap, resetAutostart, onClose])

  const currentPrice = coinSub?.plan ? PLAN_TO_PRICE[coinSub.plan] : undefined
  const visibleTiers =
    currentPrice !== undefined
      ? SUBSCRIPTION_TIERS.filter(tier => tier.priceUsd > currentPrice)
      : SUBSCRIPTION_TIERS

  return (
    <>
      <BackgroundGraphic />

      <div className="relative min-h-0 flex grow shrink flex-col overflow-clip max-h-full">
        <section className="mb-2 shrink-0 pt-6 px-4 md:px-6">
          <p className="typography-14 mb-4 font-semibold text-white">
            {t('studio.coins.top_up_dialog.title')}
          </p>
          {visibleTiers.length > 0 && (
            <>
              <h2 className="text-24 lg:text-[32px] font-poppins mb-1 text-gray-50">
                {t('studio.coins.top_up_dialog.subscriptions.heading')}
              </h2>
              <p className="typography-14 mb-6 text-gray-400">
                {t('studio.coins.top_up_dialog.subscriptions.description')}
              </p>
            </>
          )}
        </section>

        <section className="scroller-xs grow shrink min-h-0 px-4 md:px-6">
          {visibleTiers.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 md:gap-2 lg:gap-4">
              {visibleTiers.map((tier, index) => (
                <SubscriptionCard
                  key={tier.coins}
                  coins={tier.coins}
                  price={formatCurrency(tier.priceUsd, displayCurrencyCode)}
                  unitPrice={t('studio.coins.top_up_dialog.unit_price', {
                    price: formatUnitPrice(tier.priceUsd, tier.coins, displayCurrencyCode),
                    unit: (1000).toLocaleString(),
                  })}
                  variant={tier.variant}
                  plan={tier.plan}
                  onSelectPlan={selectPlan}
                  isUpgrade={isSubscribed}
                  loading={isPending}
                  success={successPlan === tier.plan}
                  isHighestValue={index === visibleTiers.length - 1}
                />
              ))}
            </div>
          )}

          <OneTimeCoinPacks />
        </section>
      </div>
      <Dialog.Close className="absolute top-6 right-6 pointer-events-auto" asChild>
        <Button iconOnly variant="overlay" size="xs" aria-label="Close">
          <XCloseIcon />
        </Button>
      </Dialog.Close>
    </>
  )
}

function OneTimeCoinPacks() {
  const t = useStudioTranslate()
  const { prices, currency } = usePricesForUser()
  const currencyCode = currency.code ?? CurrencyCode.Usd
  return (
    <>
      <h2 className="text-[32px] font-poppins mb-1 text-center text-white mt-8">
        {t('studio.coins.top_up_dialog.one_time.heading')}
      </h2>
      <p className="typography-14 mb-4 text-center text-gray-400">
        {t('studio.coins.top_up_dialog.one_time.description')}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 md:gap-2 lg:gap-4 mb-6">
        {ONE_TIME_PACKS.map((pack, index) => (
          <OneTimePackCard
            key={pack.coinPack}
            coinPack={pack.coinPack}
            coins={pack.coins}
            priceUsd={Math.round(prices[pack.priceKey] * 100)}
            currencyCode={currencyCode}
            isHighestValue={index === ONE_TIME_PACKS.length - 1}
          />
        ))}
      </div>
    </>
  )
}

function BackgroundGraphic() {
  return (
    <div className="absolute inset-0 size-full pointer-events-none overflow-clip">
      <svg
        className="-top-48 -right-64 absolute"
        width="671"
        height="542"
        viewBox="0 0 671 542"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter="url(#filter0_f_26945_110647)">
          <circle cx="419.325" cy="290.633" r="75.0707" fill="#6B1FFD" />
        </g>
        <g filter="url(#filter1_f_26945_110647)">
          <circle cx="250.95" cy="250.95" r="75.0707" fill="#9811AB" />
        </g>
        <defs>
          <filter
            id="filter0_f_26945_110647"
            x="168.374"
            y="39.6826"
            width="501.9"
            height="501.9"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="87.9399" result="effect1_foregroundBlur_26945_110647" />
          </filter>
          <filter
            id="filter1_f_26945_110647"
            x="-0.000946045"
            y="-0.000946045"
            width="501.9"
            height="501.9"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="87.9399" result="effect1_foregroundBlur_26945_110647" />
          </filter>
        </defs>
      </svg>
      <img
        alt=""
        src={COIN_GRAPHIC}
        className="-top-14 -right-16 absolute w-[512px] object-contain "
      />
    </div>
  )
}

const SHOW_SUCCESS_TIME = 5000

function OneTimePackCard({
  coinPack,
  coins,
  priceUsd,
  currencyCode,
  isHighestValue,
}: {
  coinPack: CoinPack
  coins: number
  priceUsd: number
  currencyCode: CurrencyCode
  isHighestValue: boolean
}) {
  const [showSuccess, setShowSuccess] = React.useState(false)
  const { trigger } = useCoinTransferAnimation()
  const cardRef = React.useRef<HTMLDivElement>(null)

  function onSuccess() {
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), SHOW_SUCCESS_TIME)
    trigger({ source: cardRef.current })
  }

  const { handleClick, isPending } = usePurchaseCoins({
    onSuccess,
    coinPack,
    coinsAmount: coins,
    successToastLabel: 'Your purchase has been completed',
  })

  const t = useStudioTranslate()

  return (
    <CoinPackageCard
      ref={cardRef}
      coins={coins}
      price={formatCurrency(priceUsd, currencyCode)}
      unitPrice={t('studio.coins.top_up_dialog.unit_price', {
        price: formatUnitPrice(priceUsd, coins, currencyCode),
        unit: (1000).toLocaleString(),
      })}
      onPurchase={handleClick}
      loading={isPending}
      success={showSuccess}
      isHighestValue={isHighestValue}
    />
  )
}
